import { Injectable } from '@angular/core';
import { QueryConstraint, Timestamp, where } from '@angular/fire/firestore';
import { Observable, from, map, mergeMap } from 'rxjs';
import { DbService } from 'src/app/core/services/db.service';
import { toDate } from 'src/app/shared/utils/firestore-timestamp-utils';
import { v4 as uuidv4 } from 'uuid';
import {
  PackingList,
  PackingListFirebase,
  PackingListFirebaseDocument,
  PackingListItem,
  PackingListItemFirebase,
} from '../models/packing-list.model';

@Injectable({
  providedIn: 'root',
})
export class PackingListsService {
  constructor(private db: DbService) {}

  getUserPackingLists(userId: string): Observable<PackingList[]> {
    const constraints: QueryConstraint[] = [where('ownerId', '==', userId)];

    return this.db
      .collection$<PackingListFirebase>('packingLists', constraints)
      .pipe(
        map((packingLists) =>
          packingLists.map((packingList) =>
            this.transformFromFirebase(packingList)
          )
        )
      );
  }

  getPackingList(userId: string, listId: string): Observable<PackingList | null> {
    return this.db.doc$<PackingListFirebase>(`packingLists/${listId}`).pipe(
      map((packingList) => {
        if (packingList.ownerId !== userId) {
          return null;
        }

        return this.transformFromFirebase(packingList);
      })
    );
  }

  createPackingList(userId: string, name: string): Observable<PackingList> {
    const now = new Date();
    const newPackingList: Omit<PackingList, 'id'> = {
      ownerId: userId,
      name: name.trim(),
      items: [],
      createdAt: now,
      updatedAt: now,
    };

    return from(
      this.db.updateAt(
        'packingLists',
        this.transformToFirebaseDocument(newPackingList)
      )
    ).pipe(
      map((result) => ({
        ...newPackingList,
        id: result.id || '',
      }))
    );
  }

  renamePackingList(
    userId: string,
    listId: string,
    name: string
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const trimmedName = name.trim();

        if (!trimmedName) {
          throw new Error('Packing list name is required');
        }

        const updatedPackingList: PackingList = {
          ...packingList,
          name: trimmedName,
          updatedAt: new Date(),
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  deletePackingList(userId: string, listId: string): Observable<void> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap(() => from(this.db.delete(`packingLists/${listId}`)))
    );
  }

  addPackingListItem(
    userId: string,
    listId: string,
    label: string
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const now = new Date();
        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: now,
          items: [
            ...packingList.items,
            {
              id: uuidv4(),
              label: label.trim(),
              completedAt: null,
              createdAt: now,
              updatedAt: now,
            },
          ],
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  togglePackingListItem(
    userId: string,
    listId: string,
    itemId: string
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const now = new Date();
        const itemExists = packingList.items.some((item) => item.id === itemId);

        if (!itemExists) {
          throw new Error('Packing list item not found');
        }

        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: now,
          items: packingList.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  completedAt: item.completedAt ? null : now,
                  updatedAt: now,
                }
              : item
          ),
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  renamePackingListItem(
    userId: string,
    listId: string,
    itemId: string,
    label: string
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const trimmedLabel = label.trim();

        if (!trimmedLabel) {
          throw new Error('Packing list item label is required');
        }

        const now = new Date();
        let itemWasRenamed = false;

        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: now,
          items: packingList.items.map((item) => {
            if (item.id !== itemId) {
              return item;
            }

            itemWasRenamed = true;

            return {
              ...item,
              label: trimmedLabel,
              updatedAt: now,
            };
          }),
        };

        if (!itemWasRenamed) {
          throw new Error('Packing list item not found');
        }

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  deletePackingListItem(
    userId: string,
    listId: string,
    itemId: string
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const nextItems = packingList.items.filter((item) => item.id !== itemId);

        if (nextItems.length === packingList.items.length) {
          throw new Error('Packing list item not found');
        }

        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: new Date(),
          items: nextItems,
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  bulkDeletePackingListItems(
    userId: string,
    listId: string,
    itemIds: string[]
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        const itemIdsToDelete = new Set(itemIds);

        if (!itemIdsToDelete.size) {
          throw new Error('No packing list items selected');
        }

        const nextItems = packingList.items.filter(
          (item) => !itemIdsToDelete.has(item.id)
        );

        if (nextItems.length === packingList.items.length) {
          throw new Error('Packing list items not found');
        }

        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: new Date(),
          items: nextItems,
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  reorderPackingListItems(
    userId: string,
    listId: string,
    itemIds: string[]
  ): Observable<PackingList> {
    return this.getOwnedPackingList(userId, listId).pipe(
      mergeMap((packingList) => {
        if (packingList.items.length !== itemIds.length) {
          throw new Error('Packing list reorder is invalid');
        }

        const itemMap = new Map(
          packingList.items.map((item) => [item.id, item] as const)
        );

        const reorderedItems = itemIds.map((itemId) => {
          const item = itemMap.get(itemId);

          if (!item) {
            throw new Error('Packing list reorder is invalid');
          }

          return item;
        });

        const updatedPackingList: PackingList = {
          ...packingList,
          updatedAt: new Date(),
          items: reorderedItems,
        };

        return this.persistPackingList(updatedPackingList);
      })
    );
  }

  private getOwnedPackingList(
    userId: string,
    listId: string
  ): Observable<PackingList> {
    return this.getPackingList(userId, listId).pipe(
      map((packingList) => {
        if (!packingList) {
          throw new Error('Packing list not found');
        }

        return packingList;
      })
    );
  }

  private persistPackingList(packingList: PackingList): Observable<PackingList> {
    return from(
      this.db.updateAt(
        `packingLists/${packingList.id}`,
        this.transformToFirebaseDocument(packingList)
      )
    ).pipe(map(() => packingList));
  }

  private transformFromFirebase(
    packingList: PackingListFirebase
  ): PackingList {
    return {
      id: packingList.id,
      ownerId: packingList.ownerId,
      name: packingList.name,
      createdAt: toDate(packingList.createdAt),
      updatedAt: toDate(packingList.updatedAt),
      items: packingList.items.map((item) => this.transformItemFromFirebase(item)),
    };
  }

  private transformItemFromFirebase(
    item: PackingListItemFirebase
  ): PackingListItem {
    return {
      id: item.id,
      label: item.label,
      completedAt: item.completedAt ? toDate(item.completedAt) : null,
      createdAt: toDate(item.createdAt),
      updatedAt: toDate(item.updatedAt),
    };
  }

  private transformToFirebaseDocument(
    packingList: PackingList | Omit<PackingList, 'id'>
  ): PackingListFirebaseDocument {
    const { ownerId, name, items, createdAt, updatedAt } = packingList;

    return {
      ownerId,
      name,
      items: items.map((item) => this.transformItemToFirebase(item)),
      createdAt: Timestamp.fromDate(createdAt),
      updatedAt: Timestamp.fromDate(updatedAt),
    };
  }

  private transformItemToFirebase(
    item: PackingListItem
  ): PackingListItemFirebase {
    return {
      id: item.id,
      label: item.label,
      completedAt: item.completedAt
        ? Timestamp.fromDate(item.completedAt)
        : null,
      createdAt: Timestamp.fromDate(item.createdAt),
      updatedAt: Timestamp.fromDate(item.updatedAt),
    };
  }
}
